import { Payment } from "types/payment";

export type PaymentKind = "sale" | "redeem" | "refund";

export const PAYMENT_KIND_LABEL: Record<PaymentKind, string> = {
  sale: "Sale",
  redeem: "Redeem",
  refund: "Refund",
};

/**
 * Classify a payment from its direction and destination:
 *  - `in`  → sale   (a customer paid the merchant)
 *  - `out` → redeem (sent to the treasury) or refund (sent to anyone else)
 *
 * `redeemAddress` is the treasury sink, served by GET /appConfig (see
 * features/config/useAppConfig) — never hardcoded here. Compared
 * case-insensitively: Etherscan returns checksummed addresses. It is `undefined`
 * until the config lands, which reads every "out" as a refund, so callers gate
 * rendering on the config query rather than showing that intermediate state.
 */
export function getPaymentKind(
  payment: Payment,
  redeemAddress: string | undefined,
): PaymentKind {
  if (payment.dir === "in") return "sale";
  return redeemAddress &&
    payment.to?.toLowerCase() === redeemAddress.toLowerCase()
    ? "redeem"
    : "refund";
}

export const isSale = (
  p: Payment,
  redeemAddress: string | undefined,
): boolean => getPaymentKind(p, redeemAddress) === "sale";
export const isRefund = (
  p: Payment,
  redeemAddress: string | undefined,
): boolean => getPaymentKind(p, redeemAddress) === "refund";
export const isRedeem = (
  p: Payment,
  redeemAddress: string | undefined,
): boolean => getPaymentKind(p, redeemAddress) === "redeem";

/**
 * Signed revenue contribution for analytics:
 *  - sale   → +value  (a customer paid us)
 *  - refund → −value  (we paid a customer back)
 *  - redeem →  0       (VIP cashed out to the treasury — never revenue)
 * All analytics (KPIs, revenue chart, peak hours, top customers) net sales
 * against refunds and ignore redeems entirely.
 */
export function revenueContribution(
  p: Payment,
  redeemAddress: string | undefined,
): number {
  const kind = getPaymentKind(p, redeemAddress);
  if (kind === "sale") return p.value;
  if (kind === "refund") return -p.value;
  return 0;
}

/**
 * The customer's address on a payment: the sender of a sale, the recipient of a
 * refund. (A redeem has no customer — it goes to the treasury.)
 */
export function customerAddress(
  p: Payment,
  redeemAddress: string | undefined,
): string {
  return isSale(p, redeemAddress) ? p.from : p.to;
}
